import { useCallback } from 'react'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useLoadAllItems } from '@sequencehq/api/utils'

type ApiCreditNote = Dashboardv99990101Api.GetCreditNote.GetCreditNoteResponse
type UseFetchDataForLocalPdfCreditNote = () => {
  fetchData: ({ creditNoteId }: { creditNoteId: string }) => Promise<{
    creditNote: Dashboardv99990101Api.GetCreditNote.GetCreditNoteResponse
    lineItems: Dashboardv99990101Api.GetCreditNoteLineItems.GetCreditNoteLineItemsResponse['items']
    lineItemGroups: Dashboardv99990101Api.GetCreditNoteLineItemGroups.GetCreditNoteLineItemGroupsResponse['items']
    merchantDetails: DashboardApi20240730.GetInvoiceMerchantDetails.InvoiceMerchantDetails
    merchant: DashboardApi20240730.GetMerchantForSequenceAccount.Merchant
  }>
}

export const useFetchDataForLocalPdfCreditNote: UseFetchDataForLocalPdfCreditNote =
  () => {
    /**
     * Credit note
     */
    const getCreditNote = useCallback(
      async (args: { creditNoteId: ApiCreditNote['id'] }) =>
        dashboardv99990101Client
          .getCreditNote({ id: args.creditNoteId })
          .then(response => response.data),
      []
    )

    /**
     * Line items and groups — load ALL pages, not just the first. The PDF
     * renders every item it is given, so a single-page fetch would silently
     * truncate large credit notes (matches the edit page's all-pages loading).
     */
    const loadAllLineItems = useLoadAllItems(
      dashboardv99990101Client.getCreditNoteLineItems
    )
    const loadAllLineItemGroups = useLoadAllItems(
      dashboardv99990101Client.getCreditNoteLineItemGroups
    )

    /**
     * Merchant details
     */
    const getMerchantDetails = useCallback(
      async (args: {
        currency: ApiCreditNote['currency']
        country: NonNullable<ApiCreditNote['customerBillingAddress']>['country']
        state: NonNullable<ApiCreditNote['customerBillingAddress']>['state']
      }) =>
        dashboard20240730Client
          .getInvoiceMerchantDetails({
            currency: args.currency,
            country: args.country,
            state: args.state
          })
          .then(response => response.data),
      []
    )

    /**
     * Merchant
     */
    const getMerchant = useCallback(
      async () =>
        dashboard20240730Client
          .getMerchantForSequenceAccount()
          .then(response => response.data),
      []
    )

    /**
     * Fetch data
     */
    const fetchData = useCallback(
      async ({ creditNoteId }: { creditNoteId: ApiCreditNote['id'] }) => {
        const fetchedCreditNote = await getCreditNote({ creditNoteId })

        if (!fetchedCreditNote) {
          throw new Error('fetchedCreditNote is undefined')
        }

        const fetchedLineItems = await loadAllLineItems({ creditNoteId })
        const fetchedLineItemGroups = await loadAllLineItemGroups({
          creditNoteId
        })
        const fetchedMerchant = await getMerchant()

        if (!fetchedMerchant) {
          throw new Error('fetchedMerchant is undefined')
        }

        // A credit note for an address-less customer has no country to pick
        // the merchant's payment details by, so fall back to the merchant's
        // own country.
        const fetchedMerchantDetails = await getMerchantDetails({
          currency: fetchedCreditNote.currency,
          country:
            fetchedCreditNote.customerBillingAddress?.country ??
            fetchedMerchant.address.country,
          state: fetchedCreditNote.customerBillingAddress?.state
        })

        if (!fetchedMerchantDetails) {
          throw new Error('Incomplete data to generate credit note PDF locally')
        }

        return {
          creditNote: fetchedCreditNote,
          lineItems: fetchedLineItems,
          lineItemGroups: fetchedLineItemGroups,
          merchantDetails: fetchedMerchantDetails,
          merchant: fetchedMerchant
        }
      },
      [
        getCreditNote,
        loadAllLineItemGroups,
        loadAllLineItems,
        getMerchant,
        getMerchantDetails
      ]
    )

    return { fetchData }
  }
